import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { MemberLookupSection } from '@components/lookup/MemberLookupSection';
import { PcpLookupSection } from '@components/lookup/PcpLookupSection';
import { EnrouteSection } from '@components/lookup/EnrouteSection';
import { ResultsSection } from '@components/lookup/ResultsSection';
import { ReasonForAccessModal } from '@components/lookup/ReasonForAccessModal';
import { NotByCampaignNote } from '@components/CampaignScopeNote';
import { lookupAudit, type LookupTab } from '@lib/care-api';
import { fmtMemberId } from '@lib/member-id';
import { readMemberDeepLink, stripMemberDeepLink } from '@lib/member-deep-link';
import {
  ENTRY_SCOPE, ENTRY_SCOPES, clearSessionReason, grantOpensLookup, useSessionGrant,
} from '@lib/useSessionReason';

/**
 * Lookup - the unified records page (nav 05), the Member Care clone of the Portal Operations Lookup.
 *
 * ROW 418 (William, 19 Sep 2026, verbatim): "Make this lookup just like the one for the main portal -
 * and it has to operate the same way." Measured against Frontend's as-built spec of 9:57 PM and the
 * Portal source it names (v1/src/pages/operations/MemberLookup.tsx), this page is now the Portal's
 * shell on Care's data:
 *
 *   THE TABS      Member, PCP, Enroute, Results (the Portal's four; its Event tab is hidden there
 *                 too, and its Exceptions group is Care's own Exceptions screens, nav 10 to 13).
 *                 Care's Shipping tab is FOLDED INTO ENROUTE: the kits are Enroute's kits view (the
 *                 Portal's columns) and the mailed letters are Enroute's Letters sub-tab, as on the
 *                 Portal (`?tab=shipping` still lands there).
 *   THE GATE      the reason-for-access flow is the Portal's two halves: a banner above the tabs
 *                 (green "active, scope, reason" with Clear; amber "no active reason, you will be
 *                 prompted on first search") and a MODAL asking a scope and a reason of at least 4
 *                 characters, prompted on first PHI read (mandatory on mount) rather than typed in a
 *                 banner up front. Care's server-side gate is untouched: every PHI route still refuses
 *                 without a reason and audits; the modal is the visible half and the scope rides the
 *                 audit payload. An EXPORT always re-prompts for its own export-scoped reason and runs
 *                 with that reason without disturbing the entry grant (`exportProceed`).
 *   THE LINK      `?member=<id>` (or `?member_id=`) opens that member's card on the Member tab after
 *                 the reason gate, against the loaded roster; an id not on the roster says so; the
 *                 parameter is stripped once read; the open is audited as a search via link.
 *   THE BEACONS   a settled search and a committed filter change each fire one audit row through
 *                 lookup-audit (never per keystroke), the Portal's ops-event beacons.
 *
 * The whole population of each tab loads once behind the grant, then search, slicers and sort are
 * client-side and instant over rows kept in memory, in a virtualized table with a sticky header
 * inside its own 65vh box, with a sticky right rail of slicers (280 px, collapsible to 40 px, the
 * state remembered per browser): the Portal's design, which Care has carried since July.
 */

type TabKey = 'member' | 'pcp' | 'enroute' | 'results';

export const LOOKUP_TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'member', label: 'Member' },
  { key: 'pcp', label: 'PCP' },
  { key: 'enroute', label: 'Enroute' },
  { key: 'results', label: 'Results' },
];

const TAB_DESCRIPTION: Record<TabKey, string> = {
  member: 'The whole roster; search by member ID, name or tracking number; expand a row for the full record.',
  pcp: 'One row per PCP with the panel rollups; expand a row for the panel and the PCP\'s letters and faxes.',
  enroute: 'Population-wide USPS shipment activity, one row per kit, and the mailed letters on the second sub-tab.',
  results: 'One row per resulted member: the lab milestones and the result. Opening this tab is an audited outcome disclosure.',
};

/** The tab a `?tab=` value names; the retired Shipping tab lands on Enroute. */
export function tabFromParam(v: string | null): TabKey {
  if (v === 'pcp' || v === 'enroute' || v === 'results') return v;
  if (v === 'shipping' || v === 'letters') return 'enroute';
  return 'member';
}

export function Lookup() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const tab: TabKey = tabFromParam(params.get('tab'));
  const setTab = (t: TabKey) => {
    const next = new URLSearchParams(params);
    if (t === 'member') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };

  const grant = useSessionGrant();
  const hasGrant = grantOpensLookup(grant);
  const [reasonOpen, setReasonOpen] = useState(false);
  // The export re-prompt: a section hands a continuation; a SEPARATE export-scoped modal runs it
  // with the freshly given export reason and the session grant is never touched.
  const [exportProceed, setExportProceed] = useState<((reason: string) => void) | null>(null);

  // Cross-tab drill focus: a member's PCP jumps to the PCP tab and opens that row; a panel member
  // jumps back to the Member tab and opens hers.
  const [focusMember, setFocusMember] = useState<{ id: string; via: 'link' | 'jump' } | null>(null);
  const [focusPcp, setFocusPcp] = useState<string | null>(null);
  const [deepLinkMiss, setDeepLinkMiss] = useState<string | null>(null);

  // The member link: read ONCE on mount, then stripped from the address bar so a refresh does not
  // re-open the card. Resolved by the Member tab only after the roster the operator is allowed to
  // see has loaded (the gate is untouched).
  useEffect(() => {
    const linked = readMemberDeepLink(location.search);
    if (!linked) return;
    setFocusMember({ id: linked, via: 'link' });
    const stripped = stripMemberDeepLink(location.search);
    const next = new URLSearchParams(stripped);
    next.delete('tab');
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : '' }, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The entry gate: on first mount with no grant, the modal opens as mandatory so the operator
  // records a reason before any roster is loaded (the Portal's N6).
  useEffect(() => {
    if (!hasGrant) setReasonOpen(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requireExportReason = useCallback((proceed: (reason: string) => void) => {
    setExportProceed(() => proceed);
  }, []);
  const auditSearch = useCallback((t: LookupTab, q: string) => lookupAudit(t, 'search', { query: q }), []);
  const auditFilter = useCallback((t: LookupTab, filters: Record<string, string[]>) => lookupAudit(t, 'filter', { filters }), []);

  const jumpToPcp = (key: string) => {
    setFocusPcp(key);
    setTab('pcp');
    window.scrollTo({ top: 0 });
  };
  const jumpToMember = (id: string) => {
    setDeepLinkMiss(null);
    setFocusMember({ id, via: 'jump' });
    setTab('member');
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">Member Care · Records</div>
        <h1 className="section-title">
          <em>Lookup</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          Browse the whole campaign the way the main portal does: the member roster, the PCP panel
          rollups, every kit in transit and the mailed letters, and the resulted members, with instant
          search and slicers over the rows already loaded. Expand any row into the full record.
          Read-only; every roster load, reveal and export is audited with your reason for access.
        </p>
      </header>
      {/* Row 354: its rows carry no campaign yet, so a campaign selection is SAID not to apply here. */}
      <NotByCampaignNote screen="Lookup" />

      <div role="tablist" aria-label="Lookup tabs" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
        {LOOKUP_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === tab}
            className={cn('chip', t.key === tab && 'selected')}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 12 }}>{TAB_DESCRIPTION[tab]}</p>

      {/* The reason-for-access banner, the Portal's two states. */}
      {hasGrant ? (
        <div className="note" role="status" data-testid="reason-banner-active" style={{ marginBottom: 14, borderLeftColor: 'var(--emerald)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span className="small">
            Reason-for-access active · scope={grant.scope} · &quot;{grant.reason}&quot;
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              clearSessionReason();
              setReasonOpen(true);
            }}
          >
            Clear
          </button>
        </div>
      ) : (
        <div className="note warn" role="status" data-testid="reason-banner-prompt" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span className="small">
            No active reason-for-access for scope=<code>{ENTRY_SCOPE}</code>. You&apos;ll be prompted on first search.
          </span>
          <button type="button" className="btn btn-sm" onClick={() => setReasonOpen(true)}>Provide reason</button>
        </div>
      )}

      {/* The member link landed on a member this roster does not carry. */}
      {tab === 'member' && deepLinkMiss && (
        <div role="status" className="note warn" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>Member {fmtMemberId(deepLinkMiss)} is not on this roster.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeepLinkMiss(null)}>Dismiss</button>
        </div>
      )}

      {tab === 'member' && (
        <MemberLookupSection
          onNeedReason={() => setReasonOpen(true)}
          onRequireExportReason={requireExportReason}
          onAuditSearch={(q) => auditSearch('member', q)}
          onAuditFilter={(f) => auditFilter('member', f)}
          onJumpToPcp={jumpToPcp}
          focusMember={focusMember}
          onFocusHandled={(hit) => {
            if (focusMember && !hit) setDeepLinkMiss(focusMember.id);
            setFocusMember(null);
          }}
        />
      )}
      {tab === 'pcp' && (
        <PcpLookupSection
          onNeedReason={() => setReasonOpen(true)}
          onRequireExportReason={requireExportReason}
          onAuditSearch={(q) => auditSearch('pcp', q)}
          onAuditFilter={(f) => auditFilter('pcp', f)}
          onJumpToMember={jumpToMember}
          focusPcp={focusPcp}
          onFocusHandled={() => setFocusPcp(null)}
        />
      )}
      {tab === 'enroute' && (
        <EnrouteSection
          onNeedReason={() => setReasonOpen(true)}
          onRequireExportReason={requireExportReason}
          onAuditSearch={auditSearch}
          onAuditFilter={auditFilter}
          onJumpToMember={jumpToMember}
        />
      )}
      {tab === 'results' && (
        <ResultsSection
          onNeedReason={() => setReasonOpen(true)}
          onRequireExportReason={requireExportReason}
          onAuditSearch={(q) => auditSearch('results', q)}
          onAuditFilter={(f) => auditFilter('results', f)}
          onJumpToPcp={jumpToPcp}
        />
      )}

      {/* The entry gate: mandatory until a session reason is logged; the grant persists for the SPA session. */}
      {reasonOpen && !exportProceed && (
        <ReasonForAccessModal
          mandatory
          defaultScope={ENTRY_SCOPE}
          allowedScopes={ENTRY_SCOPES}
          onClose={() => setReasonOpen(false)}
        />
      )}

      {/* The export re-prompt: a SEPARATE export-scoped reason per export, handed back to the section. */}
      {exportProceed && (
        <ReasonForAccessModal
          defaultScope="export"
          onSubmitted={(g) => {
            const go = exportProceed;
            setExportProceed(null);
            go(g.reason);
          }}
          onClose={() => setExportProceed(null)}
        />
      )}
    </div>
  );
}
